import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Menu, X, User, Heart, LogOut, Shield, Sparkles, Store } from 'lucide-react';

export const Navbar = () => {
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hasVendorSession, setHasVendorSession] = useState(!!localStorage.getItem('vendorToken'));
  const location = useLocation();

  useEffect(() => {
    const syncVendorSession = () => {
      setHasVendorSession(!!localStorage.getItem('vendorToken'));
    };

    syncVendorSession();
    window.addEventListener('storage', syncVendorSession);
    window.addEventListener('vendor-session-changed', syncVendorSession);

    return () => {
      window.removeEventListener('storage', syncVendorSession);
      window.removeEventListener('vendor-session-changed', syncVendorSession);
    };
  }, []);

  const handleVendorLogout = () => {
    localStorage.removeItem('vendorToken');
    localStorage.removeItem('vendorData');
    localStorage.removeItem('vendorLastActivity');
    window.dispatchEvent(new Event('vendor-session-changed'));
    setMobileMenuOpen(false);
  };

  const navLinks = [
    { href: '/', label: 'Home' },
    { href: '/vendors', label: 'Vendors' },
    { href: '/getting-married-in-gibraltar', label: 'Getting Married' },
    { href: '/about-gibraltar', label: 'About Gibraltar' },
    { href: '/about-expo', label: 'About the Expo' },
    { href: '/blog', label: 'Blog' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 w-full glass bg-[#FAFAF9]/80 border-b border-stone-200/50">
      <nav className="container-wedding">
        <div className="flex items-center justify-between h-16 md:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_0dd55fd0-9863-4d1b-bba2-dda5c99f23fb/artifacts/1ras6t7s_logo%20blk%2026%20cropped.png" 
              alt="Gibraltar Wedding Expo" 
              className="h-12 md:h-20 lg:h-24 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`nav-link ${isActive(link.href) ? 'active' : ''}`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop Auth */}
          <div className="hidden md:flex items-center gap-4">
            {isAuthenticated ? (
              <>
                <Link to="/planner">
                  <Button className="btn-primary" data-testid="nav-visible-planner-button">
                    <Sparkles className="h-4 w-4 mr-2" />
                    Wedding Planner
                  </Button>
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                      <User className="h-4 w-4" />
                      <span className="text-sm">{user?.name}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem asChild>
                      <Link to="/planner" className="flex items-center gap-2" data-testid="nav-planner">
                        <Sparkles className="h-4 w-4" />
                        Wedding Planner
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center gap-2" data-testid="nav-dashboard">
                        <Heart className="h-4 w-4" />
                        My Favourites
                      </Link>
                    </DropdownMenuItem>
                    {isAdmin && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center gap-2" data-testid="nav-admin">
                          <Shield className="h-4 w-4" />
                          Admin Panel
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} className="flex items-center gap-2 text-red-600" data-testid="nav-logout">
                      <LogOut className="h-4 w-4" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : hasVendorSession ? (
              <>
                <Link to="/vendor/dashboard">
                  <Button className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white" data-testid="nav-vendor-dashboard-button">
                    <Store className="h-4 w-4 mr-2" />
                    Vendor Dashboard
                  </Button>
                </Link>
                <Button variant="ghost" onClick={handleVendorLogout} className="text-red-600" data-testid="nav-vendor-logout-button">
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </>
            ) : (
              <Link to="/auth">
                <Button className="btn-primary" style={{ textTransform: 'none' }} data-testid="nav-login-register">
                  REGISTER/SIGN IN
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            {/* Always visible Login button on mobile when not authenticated */}
            {!isAuthenticated && !hasVendorSession && (
              <Link to="/auth" data-testid="mobile-login-btn">
                <Button className="btn-primary text-xs px-3 py-2 h-auto" style={{ textTransform: 'none' }}>
                  REGISTER/SIGN IN
                </Button>
              </Link>
            )}
            <button
              className="p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-stone-200 animate-fade-in">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`nav-link py-2 ${isActive(link.href) ? 'active' : ''}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              {isAuthenticated ? (
                <>
                  <Link
                    to="/planner"
                    className="flex items-center justify-center gap-2 rounded-full bg-[#5C0120] px-4 py-3 text-sm font-medium text-white"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-visible-planner-link"
                  >
                    <Sparkles className="h-4 w-4" />
                    Wedding Planner
                  </Link>
                  <Link
                    to="/dashboard"
                    className="nav-link py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    My Favourites
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="nav-link py-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Admin Panel
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      logout();
                      setMobileMenuOpen(false);
                    }}
                    className="nav-link py-2 text-left text-red-600"
                  >
                    Logout
                  </button>
                </>
              ) : hasVendorSession ? (
                <div className="flex flex-col gap-2 pt-2">
                  <Link to="/vendor/dashboard" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white" data-testid="mobile-vendor-dashboard-button">
                      Vendor Dashboard
                    </Button>
                  </Link>
                  <button
                    onClick={handleVendorLogout}
                    className="nav-link py-2 text-left text-red-600"
                    data-testid="mobile-vendor-logout-button"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-2 pt-2">
                  <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full btn-primary" style={{ textTransform: 'none' }}>REGISTER/SIGN IN</Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
